import { useEffect } from 'react';

import { Select, Col, Row, Typography } from 'antd';

import { useParams } from 'react-router-dom';

import classnames from 'classnames';

import { WithLoading, Form, CustomNotification } from 'common/components';

import {
  EDIT_SECTION,
  EditTabSectionWrapper,
} from 'pages/qa-spec/components/edit-tab-checker';

import EditContainer from '../container/EditContainer';

import { useGetEnumsByEnumName } from 'hooks';
import {
  useGetFreshProduce,
  useQaGetFreshProduceSnapshot,
  useSaveFreshProduce,
} from './hooks';

import {
  HighlightContent,
  PrimaryFieldSnapshot,
  RetailerSnapshot,
} from '../qa-prod-spec/snapshot/RetailerSnapshot';

import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';
import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';

import './QaFreshProduce.less';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { getFormattedPath } from '../shared/QaSpecHelper';

const PRODUCE_GRADING_ENUM = 'ProduceGradingEnum';
const RISK_CLASSIFICATION_ENUM = 'RiskClassificationEnum';
const FANCY_ENUM = 'FancyEnum';
const READY_TO_EAT_ENUM = ['Yes', 'No'];
const CLEAN_METHOD_ENUM = 'CleaningMethodEnum';

const QaFreshProduce = () => {
  const [formInstance] = Form.useForm();

  const { id: productId } = useParams();

  const { data: freshProduceData, isLoading } = useGetFreshProduce(productId);
  const { qaFreshProduceSnapshot } = useQaGetFreshProduceSnapshot();

  const freshProduceMutation = useSaveFreshProduce(productId);

  const produceGradingEnum = useGetEnumsByEnumName(PRODUCE_GRADING_ENUM);
  const riskClassificationEnum = useGetEnumsByEnumName(
    RISK_CLASSIFICATION_ENUM
  );
  const fancyEnum = useGetEnumsByEnumName(FANCY_ENUM);
  const cleaningMethodEnum = useGetEnumsByEnumName(CLEAN_METHOD_ENUM);

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const checkQasRequiredField = (fieldName) => {
    if (!requiredFields?.length) return false;

    const isRequired = requiredFields.includes(
      `qaSpecification.qaSpecFreshProduce.${fieldName}`.toLowerCase()
    );

    return isRequired;
  };

  const getRequiredRule = ({ fieldName = [], displayName }) => {
    const isQasRequiredField = checkQasRequiredField(fieldName);
    return {
      required: isQasRequiredField,
      message: `${displayName || 'This field'} is required`,
    };
  };

  const handleSave = async (toggleEditMode) => {
    try {
      const formValues = await formInstance.validateFields();
      const params = {
        productId,
        ...formValues,
      };

      freshProduceMutation.mutate(params, {
        onSuccess: () => toggleEditMode(),
      });
    } catch (error) {
      CustomNotification.error('Please resolve error(s)');
    }
  };

  const handleCancel = () => {
    formInstance.resetFields();
    formInstance.setFieldsValue({ ...freshProduceData });
  };

  useEffect(() => {
    if (freshProduceData) {
      formInstance.setFieldsValue({ ...freshProduceData });
    }
  }, [formInstance, freshProduceData]);

  return (
    <Form
      className='qa-fresh-produce'
      form={formInstance}
      style={{ paddingRight: 5 }}
      labelCol={{ flex: '160px' }}
    >
      <WithLoading loading={isLoading}>
        <EditContainer
          type={EDIT_SECTION.PROD_FRESH_PRODUCE}
          productId={productId}
          saveLoading={freshProduceMutation.isLoading}
          handleSave={handleSave}
          handleCancel={handleCancel}
          float
        >
          {(toggleEditMode) => {
            const disabled = !toggleEditMode;

            const {
              endUse,
              cookingInstructions,
              produceGrading,
              riskClassification,
              fancy,
              readytoEat,
              cleaningMethod,
            } = freshProduceData ?? {};

            const {
              endUse: endUseSnapshot,
              cookingInstructions: cookingInstructionsSnapshot,
              produceGrading: produceGradingSnapshot,
              riskClassification: riskClassificationSnapshot,
              fancy: fancySnapshot,
              readytoEat: readytoEatSnapshot,
              cleaningMethod: cleaningMethodSnapshot,
            } = qaFreshProduceSnapshot;

            return (
              <EditTabSectionWrapper.CheckerWrapper
                isEdit={toggleEditMode}
                type={EDIT_SECTION.PROD_FRESH_PRODUCE}
              >
                <Row
                  className={classnames('qa-fresh-produce__input', {
                    'qa-fresh-produce__input--edit': toggleEditMode,
                  })}
                >
                  <Col span={8}>
                    <Row>
                      <Col span={24}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={endUse}
                          snapshotValue={endUseSnapshot}
                          highlightContent={
                            <HighlightContent
                              label='End Use:'
                              value={endUseSnapshot}
                            />
                          }
                          placement='right'
                        >
                          <Form.Item
                            name='endUse'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.endUse'
                                )}
                                qaSpecFieldName='End Use'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.endUse'>
                                  <LabelForm label='End Use' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[
                              getRequiredRule({
                                fieldName: 'endUse',
                                displayName: 'End Use',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              autoSize={{ minRows: 4, maxRows: 5 }}
                              readOnly={disabled}
                              maxLength={500}
                              showCount
                              variant='textarea'
                            />
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                      <Col span={24}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={cookingInstructions}
                          snapshotValue={cookingInstructionsSnapshot}
                          highlightContent={
                            <HighlightContent
                              label='Cooking Instructions / Directions:'
                              value={cookingInstructionsSnapshot}
                            />
                          }
                          placement='right'
                        >
                          <Form.Item
                            name='cookingInstructions'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.cookingInstructions'
                                )}
                                qaSpecFieldName='Cooking Instructions / Directions'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.cookingInstructions'>
                                  <LabelForm label='Cooking Instructions / Directions' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[
                              getRequiredRule({
                                fieldName: 'cookingInstructions',
                                displayName:
                                  'Cooking Instructions / Directions',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              autoSize={{ minRows: 4, maxRows: 5 }}
                              readOnly={disabled}
                              maxLength={2000}
                              showCount
                              variant='textarea'
                            />
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={16}>
                    <Row>
                      <Col span={12}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={produceGrading}
                          snapshotValue={produceGradingSnapshot}
                          highlightContent={
                            <HighlightContent
                              label='Produce Grading:'
                              value={produceGradingSnapshot}
                            />
                          }
                        >
                          <Form.Item
                            name='produceGrading'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.produceGrading'
                                )}
                                qaSpecFieldName='Produce Grading'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.produceGrading'>
                                  <LabelForm label='Produce Grading' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            rules={[
                              getRequiredRule({
                                fieldName: 'produceGrading',
                                displayName: 'Produce Grading',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              disabled={disabled}
                              variant='select'
                            >
                              {produceGradingEnum.map(({ enumDisplayName }) => (
                                <Select.Option key={enumDisplayName}>
                                  {enumDisplayName}
                                </Select.Option>
                              ))}
                            </PrimaryFieldSnapshot>
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                      <Col span={12}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={riskClassification}
                          snapshotValue={riskClassificationSnapshot}
                          highlightContent={
                            <HighlightContent
                              label='Risk Classification:'
                              value={riskClassificationSnapshot}
                            />
                          }
                        >
                          <Form.Item
                            name='riskClassification'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.riskClassification'
                                )}
                                qaSpecFieldName='Risk Classification'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.riskClassification'>
                                  <LabelForm label='Risk Classification' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            rules={[
                              getRequiredRule({
                                fieldName: 'riskClassification',
                                displayName: 'Risk Classification',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              disabled={disabled}
                              variant='select'
                            >
                              {riskClassificationEnum.map(
                                ({ enumDisplayName }) => (
                                  <Select.Option key={enumDisplayName}>
                                    {enumDisplayName}
                                  </Select.Option>
                                )
                              )}
                            </PrimaryFieldSnapshot>
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                      <Col span={24}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={fancy}
                          snapshotValue={fancySnapshot}
                          highlightContent={
                            <HighlightContent
                              label='Fancy:'
                              value={fancySnapshot}
                            />
                          }
                        >
                          <Form.Item
                            name='fancy'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.fancy'
                                )}
                                qaSpecFieldName='Fancy'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.fancy'>
                                  <LabelForm label='Fancy' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            rules={[
                              getRequiredRule({
                                fieldName: 'fancy',
                                displayName: 'Fancy',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              disabled={disabled}
                              variant='select'
                            >
                              {fancyEnum.map(({ enumDisplayName }) => (
                                <Select.Option key={enumDisplayName}>
                                  {enumDisplayName}
                                </Select.Option>
                              ))}
                            </PrimaryFieldSnapshot>
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                      <Col span={24}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={readytoEat}
                          snapshotValue={readytoEatSnapshot}
                          highlightContent={
                            <HighlightContent
                              label='Ready to Eat:'
                              value={readytoEatSnapshot}
                            />
                          }
                        >
                          <Form.Item
                            name='readytoEat'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.readytoEat'
                                )}
                                qaSpecFieldName='Ready to Eat'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.readytoEat'>
                                  <LabelForm label='Ready to Eat' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            rules={[
                              getRequiredRule({
                                fieldName: 'readytoEat',
                                displayName: 'Ready to Eat',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              listHeight={140}
                              disabled={disabled}
                              variant='select'
                            >
                              {READY_TO_EAT_ENUM.map((value) => (
                                <Select.Option key={value}>
                                  {value}
                                </Select.Option>
                              ))}
                            </PrimaryFieldSnapshot>
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                      <Col span={24}>
                        <QaFreshProduceSnapshot
                          supplierModifiedValue={cleaningMethod}
                          snapshotValue={cleaningMethodSnapshot}
                          highlightContent={
                            <HighlightContent
                              label='Cleaning Method:'
                              value={cleaningMethodSnapshot}
                            />
                          }
                        >
                          <Form.Item
                            name='cleaningMethod'
                            label={
                              <TraceableCommentContainer
                                qaSpecName={getFormattedPath(
                                  'qaSpecification.freshProduce.cleaningMethod'
                                )}
                                qaSpecFieldName='Cleaning Method'
                              >
                                <QaSpecHelpContentLabel fieldPath='qaSpecification.freshProduce.cleaningMethod'>
                                  <LabelForm label='Cleaning Method' />
                                </QaSpecHelpContentLabel>
                              </TraceableCommentContainer>
                            }
                            rules={[
                              getRequiredRule({
                                fieldName: 'cleaningMethod',
                                displayName: 'Cleaning Method',
                              }),
                            ]}
                          >
                            <PrimaryFieldSnapshot
                              listHeight={140}
                              disabled={disabled}
                              variant='select'
                            >
                              {cleaningMethodEnum.map(({ enumDisplayName }) => (
                                <Select.Option key={enumDisplayName}>
                                  {enumDisplayName}
                                </Select.Option>
                              ))}
                            </PrimaryFieldSnapshot>
                          </Form.Item>
                        </QaFreshProduceSnapshot>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </EditTabSectionWrapper.CheckerWrapper>
            );
          }}
        </EditContainer>
      </WithLoading>
    </Form>
  );
};

const LabelForm = ({ label }) => {
  return <Typography.Text strong>{label}</Typography.Text>;
};

const QaFreshProduceSnapshot = (props) => {
  const { isSuccess } = useQaGetFreshProduceSnapshot();
  const isToggleSnapshot = useCheckSnapshotForRetailer();

  if (isSuccess && isToggleSnapshot) {
    return <RetailerSnapshot {...props} />;
  }

  return <>{props.children}</>;
};

export default QaFreshProduce;
